import { NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LayoutModule } from '@angular/cdk/layout';
import { NgxEchartsModule } from 'ngx-echarts';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatNativeDateModule } from '@angular/material/core';
import { ParamsEncodingInterceptor } from './interceptors/params-encoding.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { XRequestedWithInterceptor } from './interceptors/x-requested-with.interceptor';
import { PaginatorI18nService } from './services/paginator-i18n.service';
import { SimpleDialogComponent } from './components/simple-dialog/simple-dialog.component';
import { AuthorizationTokenInterceptor } from './interceptors/authorization-token.interceptor';

// required by ngx-translate for AOT compilation
export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BrowserAnimationsModule,
    MatNativeDateModule,
    MatDialogModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    FlexLayoutModule,
    MatProgressBarModule,
    LayoutModule,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
  ],
  exports: [
    CommonModule,
  ],
  declarations: [
    SimpleDialogComponent,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XRequestedWithInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ParamsEncodingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationTokenInterceptor,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      deps: [TranslateService],
      useClass: PaginatorI18nService,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        role: 'dialog',
        panelClass: '',
        hasBackdrop: true,
        backdropClass: '',
        disableClose: true,
        width: '425px',
        height: '',
        maxWidth: '80vw',
        data: null,
        ariaDescribedBy: null,
        ariaLabelledBy: null,
        ariaLabel: null,
        autoFocus: true,
        restoreFocus: true,
        closeOnNavigation: false,
      },
    },
  ],
  entryComponents: [
    SimpleDialogComponent,
  ],
})
/**
 * In the CoreModule we place new singleton services, guards and interceptors.
 * And also import library modules that will be used across the app but only need to be imported once.
 * Examples are an Authentication Service or LocalStorage Service, but also modules like
 * HttpClientModule, StoreModule.forRoot(…), TranslateModule.forRoot(…).
 * The CoreModule is then imported ONLY into the AppModule.
 *
 * DO NOT DECLARE APPLICATION WIDE COMPONENTS, PIPES AND DIRECTIVES HERE
 * AND DO NOT IMPORT THIS MODULE ANYWHERE EXCEPT FOR AppModule
 * For application wide components, pipes and directives use CommonModule (located in /app/shared)
 * For components, pipes and directives that used only by couple of feature-modules
 * create new module inside of /app/shared directory
 */
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule has already been loaded. Import in the AppModule only.');
    }
  }
}
